import { useMemo, useState } from 'react';

import { useCustomers, useDeals, useDeleteCustomer } from '../../api/hooks';
import type { CustomerView } from '../../api/types';
import { Button, DataTable, Modal, useToast } from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { PencilIcon, TrashIcon } from '../../components/icons';
import { errorMessage } from '../../lib/errors';
import { formatNumber } from '../../lib/format';
import { CustomerModal } from './CustomerModal';

interface CustomersTabProps {
  search: string;
}

type ModalState =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; customer: CustomerView }
  | { kind: 'delete'; customer: CustomerView };

/** Customers: list with create/edit/delete and per-customer deal counts. */
export function CustomersTab({ search }: CustomersTabProps) {
  const { showToast } = useToast();
  const customers = useCustomers({
    limit: 100,
    ...(search.trim() !== '' ? { search: search.trim() } : {}),
  });
  const deals = useDeals({ limit: 100 });
  const deleteCustomer = useDeleteCustomer();

  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const dealCounts = useMemo(() => {
    return (deals.data?.data ?? []).reduce<Map<string, number>>((accumulator, deal) => {
      const next = new Map(accumulator);
      next.set(deal.customerId, (next.get(deal.customerId) ?? 0) + 1);
      return next;
    }, new Map());
  }, [deals.data]);

  async function handleDelete(customer: CustomerView) {
    try {
      await deleteCustomer.mutateAsync(customer.id);
      showToast('Customer deleted');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not delete customer'), 'error');
    }
  }

  const columns: DataTableColumn<CustomerView>[] = [
    { key: 'name', header: 'Name' },
    { key: 'buyerType', header: 'Buyer Type' },
    { key: 'territory', header: 'Territory' },
    {
      key: 'deals',
      header: 'Deals',
      align: 'center',
      render: (row) => formatNumber(dealCounts.get(row.id) ?? 0),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <span className="rfp-icon-row">
          <button
            aria-label={`Edit ${row.name}`}
            className="rfp-iconbtn"
            onClick={() => setModal({ kind: 'edit', customer: row })}
            title="Edit customer"
            type="button"
          >
            <PencilIcon />
          </button>
          <button
            aria-label={`Delete ${row.name}`}
            className="rfp-iconbtn rfp-iconbtn--danger"
            onClick={() => setModal({ kind: 'delete', customer: row })}
            title="Delete customer"
            type="button"
          >
            <TrashIcon />
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Customers ({customers.data?.data.length ?? 0})</h2>
          <Button onClick={() => setModal({ kind: 'create' })} type="button">
            New Customer
          </Button>
        </div>
        {customers.isLoading ? (
          <p className="rfp-loading">Loading customers…</p>
        ) : (
          <DataTable
            columns={columns}
            emptyMessage="No customers yet — create your first buyer"
            rowKey={(row) => row.id}
            rows={customers.data?.data ?? []}
          />
        )}
      </section>

      {(modal.kind === 'create' || modal.kind === 'edit') && (
        <CustomerModal
          customer={modal.kind === 'edit' ? modal.customer : null}
          onClose={() => setModal({ kind: 'none' })}
        />
      )}

      {modal.kind === 'delete' && (
        <Modal
          actions={
            <>
              <Button onClick={() => handleDelete(modal.customer)} type="button">
                Delete
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Delete customer?"
        >
          <p>“{modal.customer.name}” and their contact details will be removed.</p>
        </Modal>
      )}
    </>
  );
}
